import { useState, useCallback, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn, ShieldCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';

const CAPTCHA_THRESHOLD = 3;

function generateCaptcha(): { question: string; answer: number } {
  const a = Math.floor(Math.random() * 20) + 1;
  const b = Math.floor(Math.random() * 20) + 1;
  const ops = [
    { symbol: '+', fn: (x: number, y: number) => x + y },
    { symbol: '-', fn: (x: number, y: number) => x - y },
  ];
  const op = ops[Math.floor(Math.random() * ops.length)];
  return { question: `${a} ${op.symbol} ${b} = ?`, answer: op.fn(a, b) };
}

export default function LoginPage() {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  const showCaptcha = failedAttempts >= CAPTCHA_THRESHOLD;

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
  }, []);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const validateForm = (): string | null => {
    if (!email.trim()) {
      return 'Emaili eshte i detyrueshem.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Ju lutem vendosni nje email te vlefshme.';
    }
    if (!password) {
      return 'Fjalekalimi eshte i detyrueshem.';
    }
    if (password.length < 8) {
      return 'Fjalekalimi duhet te kete te pakten 8 karaktere.';
    }
    if (showCaptcha) {
      if (!captchaInput.trim()) {
        return 'Ju lutem plotesoni verifikimin.';
      }
      if (parseInt(captchaInput, 10) !== captcha.answer) {
        refreshCaptcha();
        return 'Pergjigja e verifikimit eshte gabim. Provoni perseri.';
      }
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err: unknown) {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= CAPTCHA_THRESHOLD) {
        refreshCaptcha();
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Ndodhi nje gabim gjate hyrjes. Ju lutem provoni perseri.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-barrikadave-500 via-barrikadave-600 to-barrikadave-800 p-4">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4">
            <span className="text-barrikadave-500 font-bold text-2xl">R</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Rruga e Barrikadave</h1>
          <p className="text-barrikadave-200 mt-1 text-sm">
           
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Hyr ne panelin e administrimit</h2>
            <p className="text-sm text-gray-500 mt-1">
              Vendosni kredencialet tuaja per te hyre
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@barrikadave.al"
                autoComplete="email"
                className="input-field"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Fjalekalimi
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Vendosni fjalekalimin"
                  autoComplete="current-password"
                  className="input-field pr-10"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* CAPTCHA after failed attempts */}
            {showCaptcha && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Verifikimi i sigurise</span>
                </div>
                <p className="text-sm text-amber-600 mb-2">
                  Sa eshte <span className="font-bold text-amber-800">{captcha.question}</span>
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className="input-field flex-1"
                    placeholder="Pergjigja"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="px-3 py-2 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors"
                  >
                    Tjeter
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Duke hyre...</span>
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  <span>Hyr</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-center text-gray-400">
              Vetem perdoruesit me rolin ADMIN mund te hyjne.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
